import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { WorkflowAutomationDetail } from "../../../types";
import {
  fetchAutomation,
  saveAutomation,
  fetchConfigurationOptions,
  clearCurrentAutomation,
  clearError,
  setDraftAutomation,
  discardDraftAutomation,
  toWorkflowAutomationSaveInput,
  selectDraftAutomation,
  selectDetailLoading,
  selectSaveLoading,
  selectConfigurationOptions,
  selectConfigurationOptionsLoading,
  selectIsWorkflowAutomationDirty,
  selectWorkflowAutomationsError,
  WorkflowAutomationsRootState,
} from "../../../redux/workflowAutomations";
import { WorkflowConfigurationOptions } from "../catalog/configurationOptions";

export interface UseWorkflowAutomationResult {
  workflow: WorkflowAutomationDetail | null;
  configurationOptions: WorkflowConfigurationOptions | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  isDirty: boolean;
  setWorkflow: (workflow: WorkflowAutomationDetail) => void;
  save: () => Promise<void>;
  cancel: () => void;
}

export function useWorkflowAutomation(
  workflowId: string,
): UseWorkflowAutomationResult {
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<WorkflowAutomationsRootState, undefined, AnyAction>>();

  const workflow = useSelector((state: WorkflowAutomationsRootState) =>
    selectDraftAutomation(state)
  );
  const isDirty = useSelector((state: WorkflowAutomationsRootState) =>
    selectIsWorkflowAutomationDirty(state)
  );
  const detailLoading = useSelector((state: WorkflowAutomationsRootState) =>
    selectDetailLoading(state)
  );
  const configurationOptions = useSelector((state: WorkflowAutomationsRootState) =>
    selectConfigurationOptions(state)
  );
  const configurationOptionsLoading = useSelector(
    (state: WorkflowAutomationsRootState) => selectConfigurationOptionsLoading(state)
  );
  const saving = useSelector((state: WorkflowAutomationsRootState) =>
    selectSaveLoading(state)
  );
  const error = useSelector((state: WorkflowAutomationsRootState) =>
    selectWorkflowAutomationsError(state)
  );

  useEffect(() => {
    dispatch(fetchAutomation(workflowId));
    dispatch(fetchConfigurationOptions(undefined));
    return () => {
      dispatch(clearCurrentAutomation());
    };
  }, [dispatch, workflowId]);

  const setWorkflow = useCallback(
    (updated: WorkflowAutomationDetail) => {
      dispatch(setDraftAutomation(updated));
    },
    [dispatch]
  );

  const save = useCallback(async () => {
    if (!workflow) return;
    dispatch(clearError());
    await dispatch(
      saveAutomation({ workflowId, workflow: toWorkflowAutomationSaveInput(workflow) })
    );
  }, [dispatch, workflow, workflowId]);

  const cancel = useCallback(() => {
    dispatch(discardDraftAutomation());
    dispatch(clearError());
    navigate("/automations");
  }, [dispatch, navigate]);

  return {
    workflow,
    configurationOptions,
    loading: detailLoading || configurationOptionsLoading,
    saving,
    error,
    isDirty,
    setWorkflow,
    save,
    cancel,
  };
}
